import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IGroup } from "../../../shared/interfaces/group/IGroup";
import DeleteModal from "../../../shared/components/DeleteModal";
import { GroupService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";
import PageSizeSelect from "../../../shared/components/PageSizeSelect";
import Pagination from "../../../shared/components/Pagination";
import { IPagination } from "../../../shared/interfaces/shared/IPagination";

interface IProps {
    groups: IGroup[];
    pagination: IPagination;
    totalPages: number;
    handleUpdateList: () => void;
    changeFilterHandler: (
        name: string,
        value: string | number | boolean | number[] | null,
    ) => void;
}

const GroupList = ({
    groups,
    pagination,
    totalPages,
    handleUpdateList,
    changeFilterHandler,
}: IProps) => {
    const { t } = useTranslation();

    const [isDeleteVisible, setIsDeleteVisible] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<IGroup | undefined>(
        undefined,
    );

    const handleCloseDelete = () => {
        setIsDeleteVisible(false);
        setSelectedGroup(undefined);
    };

    const handleDeleteGroup = (id: number) => {
        GroupService.delete(id)
            .then(() => {
                enqueueSnackbar(
                    t("toasts.successfullyDeleted", {
                        name: selectedGroup?.name,
                    }),
                    {
                        variant: "success",
                    },
                );
                handleCloseDelete();
                handleUpdateList();
            })
            .catch((error) => {
                handleError(error);
                handleCloseDelete();
            });
    };

    return (
        <React.Fragment>
            <div className="table-responsive">
                <table className="table table-hover" id="pc-dt-simple">
                    <thead>
                        <tr>
                            <th>{t("group.name")}</th>
                            <th>{t("group.users")}</th>
                            <th>{t("group.institutions")}</th>
                            <th>{t("general.action")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((group) => {
                            return (
                                <tr key={group?.id}>
                                    <td>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-shrink-0">
                                                <div
                                                    className="avtar avtar-s bg-light-primary"
                                                    style={{ width: "40px", height: "40px" }}
                                                >
                                                    <i className="ph-duotone ph-users-three f-20"></i>
                                                </div>
                                            </div>
                                            <div className="flex-grow-1 ms-3">
                                                <h6 className="mb-0">{group?.name}</h6>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex flex-column">
                                            {group?.users?.slice(0, 3).map((user) => (
                                                <small key={user.id}>
                                                    {user.firstName} {user.lastName}
                                                </small>
                                            ))}
                                            {group?.users?.length > 3 && (
                                                <small className="text-muted">
                                                    +{group.users.length - 3} {t("general.more")}
                                                </small>
                                            )}
                                            {(!group?.users || group.users.length === 0) && (
                                                <small className="text-muted">-</small>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex flex-column">
                                            {group?.institutions?.slice(0, 3).map((inst) => (
                                                <small key={inst.id}>{inst.name}</small>
                                            ))}
                                            {group?.institutions?.length > 3 && (
                                                <small className="text-muted">
                                                    +{group.institutions.length - 3} {t("general.more")}
                                                </small>
                                            )}
                                            {(!group?.institutions || group.institutions.length === 0) && (
                                                <small className="text-muted">-</small>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <Link
                                            to={`/groups/${group?.id}`}
                                            className="avtar avtar-xs btn-link-secondary"
                                        >
                                            <i className="ti ti-edit f-20"></i>
                                        </Link>
                                        <Link
                                            to="#"
                                            onClick={() => {
                                                setSelectedGroup(group);
                                                setIsDeleteVisible(true);
                                            }}
                                            className="avtar avtar-xs btn-link-secondary"
                                        >
                                            <i className="ti ti-trash f-20"></i>
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <PageSizeSelect
                value={pagination.size}
                onChange={(size) => changeFilterHandler("size", size)}
            />

            <Pagination
                currentPage={pagination.page}
                totalPages={totalPages}
                onPageChange={(page) => changeFilterHandler("page", page)}
            />

            <DeleteModal
                show={isDeleteVisible}
                handleClose={handleCloseDelete}
                handleDeleteId={() => handleDeleteGroup(Number(selectedGroup?.id))}
                name={selectedGroup?.name}
            />
        </React.Fragment>
    );
};

export default GroupList;
